import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Reference } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Reference';
import { TFunction } from 'i18next';
import { RootState } from 'app/AppStore';
import { ChevronDownIcon, ChevronUpIcon, TrashAltIcon } from 'assets/icons';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { Dialog, useDisclosure } from 'components/Dialog';
import {
  addCustomBundleOrRecycling,
  addCustomLaborTime,
  addCustomOperationLaborTime,
  addCustomOperationReference,
  addCustomReference,
  getBundlesOrRecyclingLength,
  getLaborTimesLength,
  getOperationLaborTimes,
  getOperationReferences,
  getReferencesLength,
  removeAllBundlesOrRecycling,
  removeAllLaborTimes,
  removeAllReferences,
} from 'domains/estimate/Estimate.store';
import ReferenceTable from 'pages/EstimatePage/TableSection/ReferenceTable/ReferenceTable';
import { Flex, Icon, Text } from 'UI';
import BundleOrRecyclingTable from './BundleOrRecyclingTable';
import LaborTimeTable from './LaborTimeTable';
import {
  BundleOrRecyclingTableHeader,
  LaborTimeTableHeader,
  OperationLaborTimesTableHeader,
  OperationReferenceTableHeader,
  ReferenceTableHeader,
} from './tableHeaders';
import { AddRowButton, CollapsibleContent } from './TableSection.styled';

type TableSectionType =
  | 'reference'
  | 'labor_time'
  | 'bundle_or_recycling'
  | 'operation_reference'
  | 'operation_labor_time';

interface AddCustomOperationSectionPayload {
  operationId?: string;
}

export interface TableElements {
  table: JSX.Element;
  title: JSX.Element;
  header: (props: { onDeleteAll?: () => void; hideTitle?: boolean }) => JSX.Element;
  addRowLine: string;
  addEmptyRow: { payload: undefined | AddCustomOperationSectionPayload; type: string };
  deleteTable: { payload: undefined; type: string } | undefined;
  deleteAllDescription: string;
  getDataLength: (state: RootState, estimateId: string) => number;
}

export interface TableProps {
  estimateId: string;
  sparePartsView?: string;
  operationId?: string;
  'data-cy'?: string;
}

interface InvalidEstimateRowParams {
  isClientView?: boolean;
  row: Reference;
}

export function clientViewMarginValidator({ isClientView, row }: InvalidEstimateRowParams): boolean {
  const isFullDiscountPercent = !row.garagePrice && row.discountType === 'PERCENT' && row.discount === '100.00';
  const isFullDiscountValue =
    !row.garagePrice && row.discountType === 'VALUE_TO_EXTRACT' && row.discount === row.unitPrice;
  const isMarginInvalid = Number(row.margin) <= 0;
  const hasInvalidConditions = isMarginInvalid || isFullDiscountPercent || isFullDiscountValue;

  return isClientView ? isClientView && hasInvalidConditions : hasInvalidConditions;
}

function typeSwitch(
  type: TableSectionType,
  t: TFunction,
  estimateId: string,
  sparePartsView?: string,
  operationId?: string,
): TableElements {
  switch (type) {
    case 'reference':
      return {
        table: <ReferenceTable estimateId={estimateId} sparePartsView={sparePartsView} data-cy={'references-table'} />,
        title: (
          <Text type="heading2" disableGutter>
            <Trans i18nKey={'catalog.reference_plural'}>{'References'}</Trans>
          </Text>
        ),
        header: ({ onDeleteAll, hideTitle }) => (
          <ReferenceTableHeader onDeleteAll={onDeleteAll} hideTitle={hideTitle} />
        ),
        addRowLine: t('estimate.action.add_reference', 'Add other reference'),
        addEmptyRow: addCustomReference(),
        deleteTable: removeAllReferences(),
        deleteAllDescription: t(
          'estimate.action.remove_references.description',
          'This action will delete all items from the Reference list.',
        ),
        getDataLength: getReferencesLength,
      };
    case 'labor_time':
      return {
        table: <LaborTimeTable estimateId={estimateId} data-cy={'labor-time-table'} />,
        title: (
          <Text type="heading2" disableGutter>
            <Trans i18nKey={'catalog.parts.category.car_parts.labor_times'}>{'Labor times'}</Trans>
          </Text>
        ),
        header: ({ onDeleteAll, hideTitle }) => (
          <LaborTimeTableHeader onDeleteAll={onDeleteAll} hideTitle={hideTitle} />
        ),
        addRowLine: t('common.action.add_labor_time', 'Add Labor time'),
        addEmptyRow: addCustomLaborTime(),
        deleteTable: removeAllLaborTimes(),
        deleteAllDescription: t(
          'estimate.action.remove_labor_times.description',
          'This action will delete all items from the Labor time list.',
        ),
        getDataLength: getLaborTimesLength,
      };
    case 'operation_reference':
      return {
        table: <ReferenceTable estimateId={estimateId} sparePartsView={sparePartsView} operationId={operationId} />,
        title: (
          <Text type={'card_title_light'} disableGutter>
            <Trans i18nKey={'catalog.reference_plural'}>{'References'}</Trans>
          </Text>
        ),
        header: () => <OperationReferenceTableHeader />,
        addRowLine: t('estimate.action.add_reference', 'Add other reference'),
        addEmptyRow: addCustomOperationReference({ operationId }),
        deleteTable: undefined,
        deleteAllDescription: '',
        getDataLength: (state, eId) => getOperationReferences(state, { estimateId: eId, operationId }).length,
      };
    case 'operation_labor_time':
      return {
        table: <LaborTimeTable estimateId={estimateId} sparePartsView={sparePartsView} operationId={operationId} />,
        title: (
          <Text type={'card_title_light'} disableGutter>
            <Trans i18nKey={'catalog.parts.category.car_parts.labor_times'}>{'Labor times'}</Trans>
          </Text>
        ),
        header: () => <OperationLaborTimesTableHeader />,
        addRowLine: t('common.action.add_labor_time', 'Add Labor time'),
        addEmptyRow: addCustomOperationLaborTime({ operationId }),
        deleteTable: undefined,
        deleteAllDescription: '',
        getDataLength: (state, eId) => getOperationLaborTimes(state, { estimateId: eId, operationId }).length,
      };
    case 'bundle_or_recycling':
      return {
        table: <BundleOrRecyclingTable estimateId={estimateId} data-cy={'bundle-or-recycling-table'} />,
        title: (
          <Text type="heading2" disableGutter>
            <Trans i18nKey={'estimate.bundle_or_recycling.title'}>{'Bundles/Recycling'}</Trans>
          </Text>
        ),
        header: ({ onDeleteAll, hideTitle }) => (
          <BundleOrRecyclingTableHeader onDeleteAll={onDeleteAll} hideTitle={hideTitle} />
        ),
        addRowLine: t('common.action.add_bundle_or_recycling', 'Add Bundle / Waste Recycling'),
        addEmptyRow: addCustomBundleOrRecycling(),
        deleteTable: removeAllBundlesOrRecycling(),
        deleteAllDescription: t(
          'estimate.action.remove_free_bundles.description',
          'This action will delete all items from the Free bundles list.',
        ),
        getDataLength: getBundlesOrRecyclingLength,
      };
    default:
      return {
        table: <></>,
        title: <></>,
        header: () => <></>,
        addRowLine: t('common.action.no_data', 'No data available'),
        addEmptyRow: { payload: undefined, type: '' },
        deleteTable: { payload: undefined, type: '' },
        deleteAllDescription: t('common.action.no_data_description', 'No data is available for this section.'),
        getDataLength: () => 0,
      };
  }
}

export interface TableSectionProps {
  estimateId: string;
  type: TableSectionType;
  className?: string;
  sparePartsView?: string;
  'data-cy'?: string;
  operationId?: string;
  collapsible?: boolean;
}

export default function TableSection({
  estimateId,
  type,
  className,
  sparePartsView,
  'data-cy': dataCy,
  operationId,
  collapsible,
}: Readonly<TableSectionProps>) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const disclosure = useDisclosure();
  const { onOpen } = disclosure;
  const isB2B = useIsB2B();
  const tableElements = typeSwitch(type, t, estimateId, sparePartsView, operationId);
  const dataLength = useSelector((state: RootState) => tableElements.getDataLength(state, estimateId));
  const [collapsed, setCollapsed] = useState(false);

  function addRow() {
    dispatch(tableElements.addEmptyRow);
  }

  function removeAll() {
    dispatch(tableElements.deleteTable);
  }

  return (
    <>
      <div className={className} data-cy={dataCy}>
        <Flex align={'center'} gap={8}>
          {collapsible && (
            <Icon
              IconComponent={collapsed ? ChevronDownIcon : ChevronUpIcon}
              mt={5}
              size={22}
              onClick={() => setCollapsed((prev) => !prev)}
              data-cy={'table-section-collapse-toggle'}
            />
          )}
          <Flex size={1}>
            {collapsible ? (
              <>
                {tableElements.title}
                {tableElements.header({
                  onDeleteAll: tableElements.deleteTable && dataLength > 0 ? onOpen : undefined,
                  hideTitle: true,
                })}
              </>
            ) : (
              tableElements.header({
                onDeleteAll: tableElements.deleteTable && dataLength > 0 ? onOpen : undefined,
              })
            )}
          </Flex>
          {!isB2B &&
            (type === 'operation_reference' || type === 'operation_labor_time' ? (
              <Text type="base" color="clear_blue" cursor={'pointer'} onClick={addRow}>
                {tableElements.addRowLine}
              </Text>
            ) : (
              <AddRowButton size={'middle'} shape={'square'} onClick={addRow}>
                {tableElements.addRowLine}
              </AddRowButton>
            ))}
        </Flex>
        <CollapsibleContent $collapsed={collapsible ? collapsed : false} $margin={'6px 0 0 0'}>
          {dataLength > 0 && <div>{tableElements.table}</div>}
        </CollapsibleContent>
      </div>
      <Dialog
        disclosure={disclosure}
        title={t('common.dialog.actionRequired', 'Action required')}
        description={[
          tableElements.deleteAllDescription,
          t('common.dialog.confirmationQuestion', 'Would you like to confirm?'),
        ]}
        icon={TrashAltIcon}
        status={'info'}
        handleConfirm={removeAll}
      />
    </>
  );
}
